import { objectRecord } from './dto';
// The darice-session domain cookie (design §3.14 steps 6-7): written by the
// auth portal after a session mint, presence-checked by the CloudFront edge
// gate, and read by destination apps to bootstrap their authenticated shell.
// HttpOnly is deliberately absent (TX-368 recorded deviation): the portal is a
// static SPA with no server response to set it from, and §3.14 step 7 requires
// the destination app to read the cookie to present the session to AppSync.
export const SESSION_COOKIE_NAME = 'darice-session';
const PARENT_DOMAIN = 'daricestrategies.com';
function isDariceHost(hostname) {
    return hostname === PARENT_DOMAIN || hostname.endsWith(`.${PARENT_DOMAIN}`);
}
// On a daricestrategies.com host the cookie must span all subdomains and only
// travel over TLS; anywhere else (local dev, jsdom) it stays host-only.
function cookieScopeAttributes(target) {
    const attributes = ['Path=/', 'SameSite=Lax'];
    if (isDariceHost(target.hostname)) {
        attributes.push(`Domain=.${PARENT_DOMAIN}`, 'Secure');
    }
    else if (target.protocol === 'https:') {
        attributes.push('Secure');
    }
    return attributes;
}
export function buildSessionCookie(jwt, expiresAt, target) {
    if (jwt.trim().length === 0)
        throw new Error('FAIL: session cookie JWT must not be empty');
    if (!Number.isFinite(expiresAt))
        throw new Error('FAIL: session cookie expiry must be finite epoch seconds');
    return [
        `${SESSION_COOKIE_NAME}=${encodeURIComponent(jwt)}`,
        ...cookieScopeAttributes(target),
        `Expires=${new Date(expiresAt * 1000).toUTCString()}`,
    ].join('; ');
}
export function buildClearSessionCookie(target) {
    return [
        `${SESSION_COOKIE_NAME}=`,
        ...cookieScopeAttributes(target),
        'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
    ].join('; ');
}
function currentTarget() {
    return { hostname: window.location.hostname, protocol: window.location.protocol };
}
export function writeSessionCookie(jwt, expiresAt) {
    document.cookie = buildSessionCookie(jwt, expiresAt, currentTarget());
}
export function clearSessionCookie() {
    document.cookie = buildClearSessionCookie(currentTarget());
}
export function readSessionCookie(cookieHeader = document.cookie) {
    for (const pair of cookieHeader.split(';')) {
        const trimmed = pair.trim();
        if (!trimmed.startsWith(`${SESSION_COOKIE_NAME}=`))
            continue;
        const value = trimmed.slice(SESSION_COOKIE_NAME.length + 1);
        return value.length > 0 ? decodeURIComponent(value) : null;
    }
    return null;
}
export function decodeJwtPayload(jwt) {
    const parts = jwt.split('.');
    if (parts.length !== 3)
        return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');
    try {
        return objectRecord(JSON.parse(atob(padded)), 'session JWT payload');
    }
    catch {
        return null;
    }
}
// Guards a cookie-borne session JWT before the client adopts it. An absent,
// expired, or malformed cookie is the defined "no session" state (the caller
// bounces to the portal); anything adopted must carry the §3.14 claims.
export function parseSessionCookieJwt(jwt, nowEpochSeconds) {
    const payload = decodeJwtPayload(jwt);
    if (!payload)
        return null;
    const exp = payload.exp;
    if (typeof exp !== 'number' || !Number.isFinite(exp) || exp <= nowEpochSeconds)
        return null;
    const activeTenantId = payload.activeTenantId;
    if (typeof activeTenantId !== 'string' || activeTenantId.trim().length === 0)
        return null;
    const rawMemberships = payload.tenantMemberships;
    if (!Array.isArray(rawMemberships))
        return null;
    const tenantMemberships = [];
    for (const raw of rawMemberships) {
        if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
            return null;
        const { tenantId, role } = raw;
        if (typeof tenantId !== 'string' || tenantId.trim().length === 0)
            return null;
        if (typeof role !== 'string' || role.trim().length === 0)
            return null;
        tenantMemberships.push({ tenantId, role });
    }
    return { expiresAt: exp, activeTenantId, tenantMemberships };
}
//# sourceMappingURL=sessionCookie.js.map